import { ReplaySubject } from 'rxjs';

import { OriginalInputFile, RotationAndFlip } from '../types/types';
import { ProcessorMetadataUtils } from '../utils/processor-metadata.utils';
import { StringUtils } from '../utils/string-utils';
import { FileUploadRequestMetadata } from './file-upload-request-metadata';

export namespace UploadInProgressFileFactory {
    export async function newUploadInProgressFile<T>(
        file: T,
        metadata: FileUploadRequestMetadata,
        uniqueProcessType: string,
    ): Promise<UploadInProgressFile<T>> {
        const uuid = StringUtils.uuid();
        return new UploadInProgressFile<T>(
            file,
            metadata,
            uniqueProcessType,
            uuid,
        );
    }
}

export class UploadInProgressFile<T> implements UploadInProgressFile<T> {
    public file: T;
    public metadata: FileUploadRequestMetadata;
    /**
     * UUID used in FileUpload cannot be file upload's ObjectId since we use the same FileUpload for re-edit functionality
     */
    public uuid: string;
    public progress: number;
    public completed: boolean;
    public error: boolean;
    private originalInputFile: OriginalInputFile;
    public orientation: RotationAndFlip;

    private completePromiseResolver = null;
    private completePromise: Promise<any> = new Promise<UploadInProgressFile<any>>(resolve => {
        this.completePromiseResolver = resolve;
    });

    private responseReadyPromiseResolver = null;
    private responseReadyPromise: Promise<any> = new Promise<UploadInProgressFile<any>>(resolve => {
        this.responseReadyPromiseResolver = resolve;
    });

    public uniqueProcessType: string;

    private readonly _changes = new ReplaySubject<UploadInProgressFile<any>>(1);
    public readonly changes = this._changes.asObservable();

    get id() {
        return this.uuid;
    }

    constructor(file: T, metadata: FileUploadRequestMetadata, uniqueProcessType: string, uuid: string) {
        this.file = file;
        this.metadata = metadata;
        this.uuid = uuid;
        this.progress = 0;
        this.completed = false;

        this.uniqueProcessType = uniqueProcessType;
    }

    getMetadata() {
        return this.metadata;
    }

    setMetadata(metadata: FileUploadRequestMetadata) {
        this.metadata = metadata;
    }


    getFile() {
        return this.file;
    }

    getProgress() {
        return this.progress;
    }

    setProgress(progress: number) {
        this.progress = progress;
        this._changes.next(this);
    }

    getComplete() {
        return this.completed;
    }

    /**
     * Update the upload status. File might not still be ready
     */
    setComplete() {
        this.completePromiseResolver(this);
        this.completed = true;
    }

    /**
     * File upload completed but might not ready
     */
    whenComplete(): Promise<UploadInProgressFile<any>> {
        return this.completePromise;
    }

    getError() {
        return this.error;
    }

    setError() {
        this.error = true;
        this._changes.next(this);
    }

    getResponse(): OriginalInputFile {
        return this.originalInputFile;
    }

    setResponse(originalInputFile: OriginalInputFile) {
        this.originalInputFile = originalInputFile;
        this._changes.next(this);
        this.responseReadyPromiseResolver(this);
    }

    /**
     * File is complete and ready. 
     */
    whenResponseReady(): Promise<UploadInProgressFile<any>> {
        return this.responseReadyPromise;
    }

    setOrientation(orientation: RotationAndFlip) {
        this.orientation = orientation;
        this._changes.next(this);
    }

    getOrientation() {
        return this.orientation;
    }

    getCommURL(): Promise<string> {
        return this.whenComplete().then(() => {
            return ProcessorMetadataUtils.getUploadCommEndpoint(this.metadata.getProcessorMetadata(0));
        });
    }
}
